// WebGLRenderer applies fog after tone mapping and output conversion. Sample the
// actual authored backdrop in the same output space instead of fading every
// world into one flat cyan. The untextured fallback retains Three's theme fog.
const DECLARATIONS = `
uniform sampler2D bobBackdrop;
uniform float bobHasBackdrop;
uniform vec2 bobDrawingBuffer;
uniform vec4 bobBackdropCover;
`;
const FRAGMENT = `
#ifdef USE_FOG
  #ifdef FOG_EXP2
    float fogFactor = 1.0 - exp(-fogDensity * fogDensity * vFogDepth * vFogDepth);
  #else
    float fogFactor = smoothstep(fogNear, fogFar, vFogDepth);
  #endif
  vec3 atmosphereColour = fogColor;
  if (bobHasBackdrop > 0.5 && fogFactor > 0.0) {
    // The backdrop fills 104% of the frustum to avoid uncovered edge pixels.
    vec2 screenUV = (gl_FragCoord.xy / bobDrawingBuffer - 0.5) / 1.04 + 0.5;
    vec2 imageUV = screenUV * bobBackdropCover.xy + bobBackdropCover.zw;
    vec3 artwork = texture2D(bobBackdrop, clamp(imageUV, 0.0, 1.0)).rgb;
    #if defined(TONE_MAPPING)
      artwork = toneMapping(artwork);
    #endif
    atmosphereColour = linearToOutputTexel(vec4(artwork, 1.0)).rgb;
  }
  gl_FragColor.rgb = mix(gl_FragColor.rgb, atmosphereColour, fogFactor);
#endif
`;

/** Original screen-matched atmosphere for the pinned Three WebGL materials.
 * Texture ownership stays with renderer74: this owner borrows its live map and
 * releases references on clear/dispose. Physical drawing-buffer pixels and the
 * exact billboard cover transform prevent DPI/aspect seams; no RAF or GPU lease. */
export function createBobAtmosphere(THREE) {
  const uniforms = {
    bobBackdrop: { value: null },
    bobHasBackdrop: { value: 0 },
    bobDrawingBuffer: { value: new THREE.Vector2(1, 1) },
    bobBackdropCover: { value: new THREE.Vector4(1, 1, 0, 0) },
  };
  const prepared = new WeakSet();
  let disposed = false;
  function prepare(material) {
    if (disposed || material.fog === false || prepared.has(material)) return material;
    prepared.add(material);
    material.dithering = true;
    material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms);
      shader.fragmentShader =
        DECLARATIONS + shader.fragmentShader.replace('#include <fog_fragment>', FRAGMENT);
    };
    material.customProgramCacheKey = () => 'bob-backdrop-atmosphere-1';
    return material;
  }
  function setBackdrop(texture) {
    if (disposed) return;
    uniforms.bobBackdrop.value = texture || null;
    uniforms.bobHasBackdrop.value = texture ? 1 : 0;
  }
  function setViewport(width, height) {
    if (disposed || !Number.isFinite(width) || !Number.isFinite(height)) return;
    uniforms.bobDrawingBuffer.value.set(Math.max(1, width), Math.max(1, height));
  }
  function setCover(repeatX, repeatY, offsetX, offsetY) {
    if (disposed) return;
    uniforms.bobBackdropCover.value.set(repeatX, repeatY, offsetX, offsetY);
  }
  function dispose() {
    uniforms.bobBackdrop.value = null;
    uniforms.bobHasBackdrop.value = 0;
    disposed = true;
  }
  return { prepare, setBackdrop, setViewport, setCover, dispose };
}
